import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Alert,
  TextField,
  Button,
  Paper,
  Divider,
  useTheme,
  styled,
  IconButton,
} from '@mui/material';
import { Info as InfoIcon, Mic } from '@mui/icons-material';
import { ScribeHistoryItem } from './Interface';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { sanitizeNote } from '../Notes/sanitizeNote';

const ScribeDetailsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  padding: '24px',
  backgroundColor: theme.palette.background.default,
}));

const TranscriptSection = styled(Paper)(({ theme }) => ({
  padding: '24px',
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none',
  flex: 1,
  overflow: 'auto',
  fontSize: '14px',
  lineHeight: '1.6em',
  borderRadius: '0px',
}));

const ScribeDetailsPanel = ({
  scribe,
  scribeSent,
}: {
  scribe: ScribeHistoryItem;
  scribeSent: boolean;
}): JSX.Element => {
  const theme = useTheme();
  const [additionalNotes, setAdditionalNotes] = useState('');

  // Get the selected scribe data (assuming index 2 for "Initial Consultation")
  const selectedScribe = scribe;

  if (!selectedScribe?.request) {
    return (
      <ScribeDetailsContainer>
        <Typography variant="h6" color="text.secondary">
          Select a scribe session to view details
        </Typography>
      </ScribeDetailsContainer>
    );
  }

  const handleSaveNotes = () => {
    // TODO: Implement save functionality
    // Save additional notes logic will be implemented here
  };

  return (
    <Stack direction="column" height="100%" width="100%" sx={{ paddingBottom: '148px' }}>
      {/* Transcript Section */}
      <TranscriptSection>
        <div dangerouslySetInnerHTML={sanitizeNote(selectedScribe?.request?.text, true)} />
      </TranscriptSection>

      {/* Additional Notes Section - Bottom of Page */}
      {!scribeSent && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid #E0E0E0',
            borderRadius: '16px',
            background: '#fff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            padding: '0px 4px 12px 12px',
            minHeight: '108px',
            maxHeight: '108px',
            position: 'relative',
            flexDirection: 'column',
            gap: 1,
            margin: '8px 24px 24px 24px',
          }}
          data-testid="ai-ask-bar-container">
          <TextField
            fullWidth
            variant="standard"
            placeholder="Add additional notes..."
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: 16, background: 'transparent', padding: '16px 0px 0px 0px !important' },
            }}
            inputProps={{
              'aria-label': 'Ask using AI',
              'data-testid': 'ai-ask-bar-input',
              style: { padding: 0 },
            }}
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                window.console.log('submit');
              }
            }}
            multiline
            maxRows={8}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              width: '100%',
              gap: 2,
              paddingRight: 2,
              marginTop: 'auto',
              paddingBottom: '16px',
            }}>
            <Button
              variant="outlined"
              onClick={handleSaveNotes}
              startIcon={<Mic sx={{ marginRight: 0 }} />}
              sx={{ borderRadius: '4px', height: '28px', alignItems: 'center' }}>
              DICTATE
            </Button>
            <IconButton
              tabIndex={0}
              aria-label="Submit AI question"
              data-testid="ai-ask-bar-submit"
              onClick={() => {}}
              sx={{
                borderRadius: '50%',
                width: 28,
                height: 28,
                '&.MuiButtonBase-root': { backgroundColor: 'primary.main' },
              }}>
              <ArrowUpwardIcon sx={{ width: 20, height: 20, color: 'primary.contrastText' }} />
            </IconButton>
          </Box>
        </Box>
      )}
    </Stack>
  );
};

export default ScribeDetailsPanel;
